import { createReducer } from 'typesafe-actions';

import {
  IRestrictionsGroupState,
  TPredefRestrictionsGroupAction,
  PredefRestrictionsGroupActionTypes,
} from '@/store/types/predef-restrictions-groups.types';
import { RestrictionsGroupStatus } from '@/store/models/restriction.model';

const initialState: IRestrictionsGroupState = {
  restrictionsGroups: {
    mcc: {
      elements: [],
      pagination: {
        totalPages: 0,
        currentPage: 0,
      },
      isLoading: false,
    },
    mid: {
      elements: [],
      pagination: {
        totalPages: 0,
        currentPage: 0,
      },
      isLoading: false,
    },
    country: {
      elements: [],
      pagination: {
        totalPages: 0,
        currentPage: 0,
      },
      isLoading: false,
    },
  },
  isLoading: false,
  tabIdRestriction: 0,
};

export const predefRestrictionsGroupReducer = createReducer<IRestrictionsGroupState, TPredefRestrictionsGroupAction>(
  initialState,
)
  /**
   * Search Predef Restrictions Groups reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.SEARCH_PREDEF_RESTRICTIONS_GROUPS, (state, action) => ({
    ...state,
    restrictionsGroups: {
      ...state.restrictionsGroups,
      [action.restrictionsGroupType]: {
        ...state.restrictionsGroups[action.restrictionsGroupType],
        isLoading: true,
      },
    },
  }))

  .handleType(PredefRestrictionsGroupActionTypes.SEARCH_PREDEF_RESTRICTIONS_GROUPS_SUCCESS, (state, action) => {
    const restrictionsGroupsOfType = action.restrictionsGroups.map((group) => ({
      group,
    }));

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          elements: restrictionsGroupsOfType,
          pagination: action.pagination,
          isLoading: false,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.SEARCH_PREDEF_RESTRICTIONS_GROUPS_FAIL, (state, action) => ({
    ...state,
    restrictionsGroups: {
      ...state.restrictionsGroups,
      [action.restrictionsGroupType]: {
        ...state.restrictionsGroups[action.restrictionsGroupType],
        error: action.error,
        isLoading: false,
      },
    },
  }))

  /**
   * Add Predef Restrictions Group reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.ADD_PREDEF_RESTRICTION_GROUP, (state) => ({
    ...state,
    isLoading: true,
    error: undefined,
  }))

  .handleType(PredefRestrictionsGroupActionTypes.ADD_PREDEF_RESTRICTION_GROUP_SUCCESS, (state, action) => {
    const restrictionsGroupsOfType = {
      ...state.restrictionsGroups[action.restrictionsGroup.type],
      elements: [
        {
          ...state.restrictionsGroups[action.restrictionsGroup.type].elements,
          group: action.restrictionsGroup,
        },
      ],
    };

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroup.type]: restrictionsGroupsOfType,
      },
      isLoading: false,
      error: undefined,
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.ADD_PREDEF_RESTRICTION_GROUP_FAIL, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  }))

  /**
   * Update Predef Restrictions Group reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTION_GROUP, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: true,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTION_GROUP_SUCCESS, (state, action) => {
    const restrictionsGroupsOfType = state.restrictionsGroups[action.restrictionsGroup.type].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroup.id) {
          return {
            ...restrictionsGroup,
            group: action.restrictionsGroup,
            isLoading: false,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroup.type]: {
          ...state.restrictionsGroups[action.restrictionsGroup.type],
          elements: restrictionsGroupsOfType,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTION_GROUP_FAIL, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: false,
            error: action.error,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  /**
   * Delete Predef Restrictions Group reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.DELETE_PREDEF_RESTRICTION_GROUP, (state) => ({
    ...state,
    isLoading: true,
    error: undefined,
  }))

  .handleType(PredefRestrictionsGroupActionTypes.DELETE_PREDEF_RESTRICTION_GROUP_SUCCESS, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId)
          restrictionsGroup.group.status = RestrictionsGroupStatus.CANCELED;
        return restrictionsGroup;
      },
    );
    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
      isLoading: false,
      error: undefined,
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.DELETE_PREDEF_RESTRICTION_GROUP_FAIL, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  }))

  /**
   * Get Predef Restrictions Group Metadata reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.GET_PREDEF_RESTRICTIONS_GROUP_METADATA, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: true,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.GET_PREDEF_RESTRICTIONS_GROUP_METADATA_SUCCESS, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            metadata: action.metadata,
            cursor: action.cursor,
            isLoading: false,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.GET_PREDEF_RESTRICTIONS_GROUP_METADATA_FAIL, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: false,
            error: action.error,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  /**
   * Update Predef Restrictions Group Metadata reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_SINGLE_METADATA, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: true,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(
    PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_SINGLE_METADATA_SUCCESS,
    (state, action) => {
      const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
        (restrictionsGroup) => {
          if (restrictionsGroup.group.id === action.restrictionsGroupId) {
            const currentMetadata = restrictionsGroup?.metadata || [];
            const newMetadata = action.metadataValue;
            const updatedMetadata: any[] = [...currentMetadata];
            const index = currentMetadata.findIndex((metadata) => metadata.mid === newMetadata.mid);
            if (index >= 0) {
              updatedMetadata[index] = newMetadata;
            } else updatedMetadata.push(newMetadata);

            return {
              ...restrictionsGroup,
              metadata: updatedMetadata,
              isLoading: false,
              error: undefined,
            };
          }

          return restrictionsGroup;
        },
      );

      return {
        ...state,
        restrictionsGroups: {
          ...state.restrictionsGroups,
          [action.restrictionsGroupType]: {
            ...state.restrictionsGroups[action.restrictionsGroupType],
            elements: restrictionsGroups,
          },
        },
      };
    },
  )

  .handleType(
    PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_SINGLE_METADATA_FAIL,
    (state, action) => {
      const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
        (restrictionsGroup) => {
          if (restrictionsGroup.group.id === action.restrictionsGroupId) {
            return {
              ...restrictionsGroup,
              isLoading: false,
              error: action.error,
            };
          }

          return restrictionsGroup;
        },
      );

      return {
        ...state,
        restrictionsGroups: {
          ...state.restrictionsGroups,
          [action.restrictionsGroupType]: {
            ...state.restrictionsGroups[action.restrictionsGroupType],
            elements: restrictionsGroups,
          },
        },
      };
    },
  )

  /**
   * Update Predef Restrictions Group Metadata reducers
   */
  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_METADATA, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: true,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_METADATA_SUCCESS, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            metadata: action.metadatas,
            isLoading: false,
            error: undefined,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_METADATA_FAIL, (state, action) => {
    const restrictionsGroups = state.restrictionsGroups[action.restrictionsGroupType].elements.map(
      (restrictionsGroup) => {
        if (restrictionsGroup.group.id === action.restrictionsGroupId) {
          return {
            ...restrictionsGroup,
            isLoading: false,
            error: action.error,
          };
        }

        return restrictionsGroup;
      },
    );

    return {
      ...state,
      restrictionsGroups: {
        ...state.restrictionsGroups,
        [action.restrictionsGroupType]: {
          ...state.restrictionsGroups[action.restrictionsGroupType],
          elements: restrictionsGroups,
        },
      },
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.SAVE_RESTRICTIONS_PATH, (state, action) => {
    return {
      ...state,
      tabIdRestriction: action.tabId,
    };
  })

  .handleType(PredefRestrictionsGroupActionTypes.RESET_RESTRICTIONS_GROUPS, (state, action) => ({
    ...state,
    restrictionsGroups: {
      ...state.restrictionsGroups,
      [action.restrictionGroupType]: initialState.restrictionsGroups[action.restrictionGroupType],
    },
  }));
